import { useCallback, useEffect, useRef, useState } from 'react'
import { useLazyQuery } from 'react-apollo'
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { CellularPayPrompt, CellularPayResult, PaymentFailure, PaymentMethodChoice } from './types'
import { Charge, usePayInvoice } from './pay-invoice'
import { AMOUNT_CHANGED_FAILURE } from './amount-changed'
import { PW_GET_SETUP_INTENT } from '../../../apollo/api/paywall/queries'

const GENERIC_FAILURE = 'We could not process your payment. Please try again.'

// The mutation resolved with no payload at all (network layer, error link, or a
// null field): we cannot tell what happened, and saying nothing leaves the
// customer clicking Pay for ever.
export const NO_RESULT_FAILURE =
  'We did not get an answer from our payment service, so we cannot tell whether your card was ' +
  'charged. Please reload the page and check before trying again.'

export const AUTHENTICATION_FAILED = 'Your bank did not confirm this payment. Please try again.'

export const AUTHENTICATION_NEEDS_PAGE =
  'Your bank needs you to confirm this payment. Please finish it on the secure invoice page.'

export const INVOICE_NOT_READY =
  'Your bank needs you to confirm this payment, and the invoice page is still being prepared. ' +
  'Please try again shortly.'

// The bank confirmed a 3-D Secure payment, but the paywall has not seen it as paid yet.
export const CONFIRMED_NOT_RECORDED =
  'Your bank confirmed this payment, but we have not finished recording it yet. Please wait a ' +
  'moment and press Pay again to finish: a bill that is already paid is never charged twice.'

// Statuses that mean "the customer must authenticate" (3-D Secure / SCA), not "declined".
const NEEDS_AUTHENTICATION = [
  'requires_action',
  'requires_source_action',
  'requires_confirmation',
  'authentication_required'
]

// The API answers with the invoice's own status ('open'), not a PaymentIntent
// status, so a client secret is the only reliable sign that the bank wants the
// customer present. The status list stays as a fallback for older responses.
const needsAuthentication = (result: CellularPayResult): boolean =>
  !!result.paymentIntentClientSecret ||
  NEEDS_AUTHENTICATION.includes((result.status || '').toLowerCase())

interface UseCellularPaymentArgs {
  accountId: string
  prompt: CellularPayPrompt
  method: PaymentMethodChoice
  // The new card's cardholder name, and whether every card field is complete.
  cardholderName: string
  cardReady: boolean
  onPaid: () => void
  // A card was attached to the customer: refetch the prompt so it shows as a choice.
  onCardSaved: () => void
}

export const useCellularPayment = ({
  accountId,
  prompt,
  method,
  cardholderName,
  cardReady,
  onPaid,
  onCardSaved
}: UseCellularPaymentArgs) => {
  const stripe = useStripe()
  const elements = useElements()
  const payInvoice = usePayInvoice(accountId, prompt)
  const [getSetupIntent, { data: setupIntentData }] = useLazyQuery(PW_GET_SETUP_INTENT, {
    fetchPolicy: 'network-only'
  })
  const [paying, setPaying] = useState(false)
  const [failure, setFailure] = useState<PaymentFailure | null>(null)
  // The card this dialog just saved through a SetupIntent. Kept so that a failed
  // payment retries the payment only: a consumed SetupIntent cannot be reused.
  const [savedCardPaymentMethodId, setSavedCardPaymentMethodId] = useState<string | null>(null)
  const requestedSetupIntent = useRef(false)
  const inFlight = useRef(false)

  const requestSetupIntent = useCallback(() => {
    requestedSetupIntent.current = true
    getSetupIntent({ variables: { accountId } })
  }, [accountId, getSetupIntent])

  useEffect(() => {
    // One SetupIntent per dialog: toggling saved <-> new used to orphan one each time.
    if (method === 'new' && !requestedSetupIntent.current) requestSetupIntent()
  }, [method, requestSetupIntent])

  const clientSecret: string | undefined =
    setupIntentData?.getCardSetupIntentByAccountId?.client_secret // eslint-disable-line camelcase

  const fail = (message: string, hostedInvoiceUrl?: string | null) =>
    setFailure({ message, hostedInvoiceUrl: hostedInvoiceUrl || prompt.hostedInvoiceUrl })

  // A genuinely new card is being entered: the SetupIntent we consumed for the
  // previous one is spent, so drop that card and ask for a fresh SetupIntent.
  const startNewCardEntry = () => {
    if (!savedCardPaymentMethodId || inFlight.current) return
    setSavedCardPaymentMethodId(null)
    requestSetupIntent()
  }

  // Saves the new card through a SetupIntent; returns its payment method id, or null on failure.
  const confirmNewCard = async (): Promise<string | null> => {
    const card = elements?.getElement(CardNumberElement)
    if (!stripe || !card || !clientSecret) {
      fail('The card form is still loading. Please try again in a moment.')
      return null
    }
    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card, billing_details: { name: cardholderName.trim() } }
    })
    if (error || !setupIntent || setupIntent.status !== 'succeeded') {
      fail(error?.message || GENERIC_FAILURE)
      return null
    }
    return setupIntent.payment_method as string
  }

  const newCardPaymentMethodId = async (): Promise<string | null> => {
    // Card saved, invoice unpaid: retry the payment with the card we already saved.
    if (savedCardPaymentMethodId) return savedCardPaymentMethodId
    const paymentMethodId = await confirmNewCard()
    if (!paymentMethodId) return null
    setSavedCardPaymentMethodId(paymentMethodId)
    onCardSaved()
    return paymentMethodId
  }

  const charge = async (): Promise<Charge> => {
    if (method === 'saved') return payInvoice(null)
    const paymentMethodId = await newCardPaymentMethodId()
    if (!paymentMethodId) return { kind: 'reported' }
    return payInvoice(paymentMethodId)
  }

  // The bank confirmed the payment here in the browser, which the paywall has not
  // seen. Asking it to pay the same invoice with the same card again never charges
  // a paid invoice: it runs the post-payment steps (the card saved on the cellular
  // subscription for automatic renewal, give-up cleared) and says it is paid. Anything else is not a failed
  // payment, so the customer is told to finish it rather than to pay again elsewhere.
  const finishAuthenticated = async (paymentMethodId: string | null): Promise<boolean> => {
    const outcome = await payInvoice(paymentMethodId).catch((): Charge => ({ kind: 'noResult' }))
    if (outcome.kind === 'result' && outcome.result.paid) {
      onPaid()
      return true
    }
    setFailure({ message: CONFIRMED_NOT_RECORDED, hostedInvoiceUrl: null })
    return false
  }

  // 3-D Secure: the card needs the customer present. Confirm it here when the API
  // hands us a client secret, otherwise send them to the hosted invoice page.
  const authenticate = async (
    result: CellularPayResult,
    paymentMethodId: string | null
  ): Promise<boolean> => {
    const paymentSecret = result.paymentIntentClientSecret
    if (stripe && paymentSecret) {
      const { error, paymentIntent } = await stripe.confirmCardPayment(paymentSecret)
      if (!error && paymentIntent?.status === 'succeeded') return finishAuthenticated(paymentMethodId)
      fail(error?.message || AUTHENTICATION_FAILED, result.hostedInvoiceUrl)
      return false
    }
    const page = result.hostedInvoiceUrl || prompt.hostedInvoiceUrl
    fail(page ? AUTHENTICATION_NEEDS_PAGE : INVOICE_NOT_READY, result.hostedInvoiceUrl)
    return false
  }

  // Returns true when the payment is done and this dialog is going away.
  const settle = async (
    result: CellularPayResult,
    paymentMethodId: string | null
  ): Promise<boolean> => {
    if (result.paid) {
      onPaid()
      return true
    }
    if (needsAuthentication(result)) return authenticate(result, paymentMethodId)
    fail(result.message || GENERIC_FAILURE, result.hostedInvoiceUrl)
    return false
  }

  const pay = async () => {
    // A second click must never start a second charge.
    if (inFlight.current) return
    inFlight.current = true
    setPaying(true)
    setFailure(null)
    try {
      const outcome = await charge()
      if (outcome.kind === 'noResult') fail(NO_RESULT_FAILURE)
      // Stripe's page would show the new amount too, but refreshing here is quicker.
      if (outcome.kind === 'amountChanged') setFailure(AMOUNT_CHANGED_FAILURE)
      if (outcome.kind === 'result' && (await settle(outcome.result, outcome.paymentMethodId))) return
    } catch (error) {
      fail(GENERIC_FAILURE)
    }
    inFlight.current = false
    setPaying(false)
  }

  // No amount to pay is not something the customer can fix by clicking Pay.
  const hasAmountDue = Number(prompt.amountDueUsd) > 0
  const canPay =
    !paying &&
    hasAmountDue &&
    (method === 'saved' || !!savedCardPaymentMethodId || (!!stripe && !!clientSecret && cardReady))

  return {
    pay,
    paying,
    failure,
    canPay,
    startNewCardEntry,
    clearFailure: () => setFailure(null)
  }
}
